import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum GGXlsxLineItemKeys {
  itemNumber = 'itemNumber',
  itemType = 'itemType',
  designName = 'designName',
  bodyStyleNumber = 'bodyStyleNumber',
  color = 'color',
  description = 'description',
  xs = 'xs',
  s = 's',
  m = 'm',
  l = 'l',
  xl = 'xl',
  xl2 = 'xl2',
  xl3 = 'xl3',
  xl4 = 'xl4',
  xl5 = 'xl5',
  otherQty = 'otherQty',
  subTotals = 'subTotals',
  price = 'price',
  total = 'total',
}

const CostsSchema = yup
  .object()
  .shape({
    subTotal: yup.string().optional().nullable(),
    price: yup.string().optional().nullable(),
  })
  .required()

export const ggPurchaseOrderFileDataXlsxSchema = yup.object().shape({
  sender: yup
    .object()
    .shape({
      addressLine1: yup.string().optional().nullable(),
      addressLine2: yup.string().optional().nullable(),
      addressLine3: yup.string().optional().nullable(),
      emailAddress: yup.string().optional().nullable(),
      phoneNumber: yup.string().optional().nullable(),
    })
    .required(),
  purchaseOrderNumber: yup.string().optional().nullable(),
  targetShipDate: yup.string().optional().nullable(),
  dateIssued: yup.string().optional().nullable(),
  cancelDate: yup.string().optional().nullable(),
  issuedTo: yup.string().optional().nullable(),
  clientName: yup.string().optional().nullable(),
  contactInfo: yup
    .object()
    .shape({
      address: yup.string().optional().nullable(),
      phoneNumber: yup.string().optional().nullable(),
      emailAddress: yup.string().optional().nullable(),
    })
    .required(),
  shipFrom: yup
    .object()
    .shape({
      name: yup.string().optional().nullable(),
      company: yup.string().optional().nullable(),
      addressLine1: yup.string().optional().nullable(),
      addressLine2: yup.string().optional().nullable(),
      phoneNumber: yup.string().optional().nullable(),
      emailAddress: yup.string().optional().nullable(),
    })
    .required(),
  shipTo: yup
    .object()
    .shape({
      name: yup.string().optional().nullable(),
      company: yup.string().optional().nullable(),
      addressLine1: yup.string().optional().nullable(),
      addressLine2: yup.string().optional().nullable(),
      emailAddress: yup.string().optional().nullable(),
    })
    .required(),
  checks: yup
    .object()
    .shape({
      pullBlanks: yup.string().optional().nullable(),
      cutSew: yup.string().optional().nullable(),
      dye: yup.string().optional().nullable(),
      wash: yup.string().optional().nullable(),
      embroider: yup.string().optional().nullable(),
      print: yup.string().optional().nullable(),
      finish: yup.string().optional().nullable(),
      package: yup.string().optional().nullable(),
      other1: yup.object().shape({
        check: yup.string().optional().nullable(),
        description: yup.string().optional().nullable(),
      }),
      other2: yup.object().shape({
        check: yup.string().optional().nullable(),
        description: yup.string().optional().nullable(),
      }),
    })
    .required(),
  items: yup
    .array()
    .of(
      yup
        .object()
        .shape({
          itemNumber: yup.number().optional().nullable(),
          itemType: yup.string().optional().nullable(),
          designName: yup.string().optional().nullable(),
          bodyStyleNumber: yup.string().optional().nullable(),
          color: yup.string().optional().nullable(),
          description: yup.string().optional().nullable(),
          xs: yup.string().optional().nullable(),
          s: yup.string().optional().nullable(),
          m: yup.string().optional().nullable(),
          l: yup.string().optional().nullable(),
          xl: yup.string().optional().nullable(),
          xl2: yup.string().optional().nullable(),
          xl3: yup.string().optional().nullable(),
          xl4: yup.string().optional().nullable(),
          xl5: yup.string().optional().nullable(),
          otherQty: yup.string().optional().nullable(),
          price: yup.string().optional().nullable(),
        })
        .required(),
    )
    .required(),
  costs: yup
    .object()
    .shape({
      finishing: CostsSchema,
      sampling: CostsSchema,
      packagingCosts: CostsSchema,
      setupCosts: CostsSchema,
      screenCosts: CostsSchema,
      additionalCost: CostsSchema,
    })
    .required(),
  instructions: yup
    .object()
    .shape({
      wash: yup.string().optional().nullable(),
      insideNeck: yup.string().optional().nullable(),
      careLabel: yup.string().optional().nullable(),
      sizeLabel: yup.string().optional().nullable(),
      trims: yup.string().optional().nullable(),
      packaging: yup.string().optional().nullable(),
      approvals: yup.string().optional().nullable(),
      sample: yup.string().optional().nullable(),
      other: yup.string().optional().nullable(),
    })
    .required(),
})

export type GGPurchaseOrderFileDataXlsx = yup.InferType<typeof ggPurchaseOrderFileDataXlsxSchema>
type RequiredFileData = MakeRequiredAndNullable<GGPurchaseOrderFileDataXlsx>

// GG XLSX Purchase Order File Mappings
export const ggPurchaseOrderXlsxFileMappings: RequiredFileData = {
  sender: {
    addressLine1: null,
    addressLine2: null,
    addressLine3: null,
    emailAddress: null,
    phoneNumber: null,
  },
  purchaseOrderNumber: '93de28ae-d64d-4247-ab18-9640becc6ae0',
  dateIssued: 'b2b25e38-d8c1-4c5d-b537-e8c6514efe8d',
  targetShipDate: '9f73fdc7-caed-451d-9305-0c606aed9fe4',
  cancelDate: '3e2e2b6f-9f4a-4e6f-9d5e-0b9f5e7d9f4e',
  issuedTo: '884231e9-8c88-49d1-a749-8a346810af52',
  clientName: '54b806ab-fef4-41bf-8cb9-96b6117b5fdd',
  contactInfo: {
    address: 'f156ba41-c9d3-484c-ae30-83a6cd1ea59b',
    phoneNumber: 'a011635e-97e5-4795-be45-76e6ebe2efa0',
    emailAddress: '38afdfa3-5627-46a4-924c-b55d497f1c16',
  },
  shipFrom: {
    name: '88056d8f-ff4c-4730-b757-82a73bb23b63',
    company: '3949b69d-ce56-44f4-a1c6-052d4f055bc0',
    addressLine1: '5285a541-ef4d-42f3-95ab-08481e287e69',
    addressLine2: '29e52d7b-e25e-4e8f-8451-1558e98f640b',
    phoneNumber: '869551ab-79e8-470f-b9af-f85ef244e5ef',
    emailAddress: 'c6202501-efa0-4604-9971-651f7647ead2',
  },
  shipTo: {
    name: 'ede95f2f-22c7-4258-bfb5-77c8dddb7b87',
    company: '81398bfc-28ae-4f33-8758-7bb44811d35d',
    addressLine1: '02857561-a3d0-4b59-87e0-c73fcc5eee69',
    addressLine2: '7d51e687-9f79-46bd-ae82-36795ec358d5',
    emailAddress: '8e3191be-f6da-4216-a22e-06ae8cc39da9',
  },
  checks: {
    pullBlanks: '6bed78ee-d662-4567-8337-9d6e32c008c0',
    cutSew: '00849858-55fe-408c-a4d7-390a173d3f15',
    dye: '70db8f22-68bd-4575-a895-8ec5e55c0208',
    wash: 'be94e967-46e7-4d13-bf6f-90fc8f13b853',
    embroider: 'bba645db-4723-45e1-860a-4a924dd4604d',
    print: '4e4fbd52-2a02-414c-a04d-c4c0ffd14d86',
    finish: '7e067bbb-45cc-4f89-a33e-6f147c2468d8',
    package: 'b9cca741-598d-4376-b151-e6a8d025024e',
    other1: {
      check: 'fdc3c7a6-42c5-4095-baa9-43fa189e6cd9',
      description: '04d16366-51e5-4bac-ba79-86c50f52c546',
    },
    other2: {
      check: 'e0b24471-fe6d-4d86-a26a-778068679f68',
      description: '92c09eda-661d-4ec0-b353-136c741ebdef',
    },
  },
  items: [
    {
      itemNumber: null,
      itemType: 'f3b3b3e7-0f9e-4b9f-8d1e-3d6a3e4f1d5c',
      designName: 'b83fe07a-4edf-4173-bad4-3d1d47ceef17',
      bodyStyleNumber: '3355fcd6-a626-458b-b4db-9e6bcb1c0bdd',
      color: '2645eb9c-5930-4197-868c-5d8038231c5a',
      description: '91db705d-0e8b-40d7-8f38-1cf20de931cd',
      xs: 'c4b46e67-2fa3-4560-b676-67e393857ec0',
      s: 'fca8ff06-fb22-4605-a1a2-f66d6180cc26',
      m: 'c366fbe3-5f28-4bdd-93b0-878317fb48e1',
      l: '32c71891-0565-4cd5-b337-87e15b0f8f37',
      xl: '443d91c7-b79b-4381-83a7-fbf7433305f2',
      xl2: '3bf5df8d-1017-4578-9edb-75af3c3fba42',
      xl3: '43f7536c-286e-48b8-b3b0-a01fa5b9ee6b',
      xl4: 'cfbe1f87-b985-4e64-8fb0-700f67bef0d2',
      xl5: '147d375e-b64a-4156-b8f9-380c48bf02a9',
      otherQty: '8efad2a1-57f8-4c69-96ae-408b47a99551',
      price: '831fd358-93ee-4415-8c60-b662cafb695d',
    },
  ],
  costs: {
    finishing: {
      subTotal: '09f95314-b5ed-40c5-bf61-3a7086f71f16',
      price: 'fb49d354-9a7c-49c4-be16-3bdf87ecda6e',
    },
    sampling: {
      subTotal: '3cc00230-c692-4dc4-b134-c5305ac60402',
      price: '5c3801d5-3307-4bd0-9de7-a8c10138a495',
    },
    packagingCosts: {
      subTotal: 'a7e546ae-5c68-41f5-9583-08511c64eb5c',
      price: '5ae211e5-eb16-4a55-abcc-f151c63355e7',
    },
    setupCosts: {
      subTotal: 'b941cb1c-45cc-42a9-8d73-5e0dd894c74a',
      price: '9db620d9-444f-4ef8-a19f-46f3b43fd94d',
    },
    additionalCost: {
      subTotal: 'e3f0e18e-e57f-4751-9c45-d136d593f579',
      price: '623ea868-3169-4884-b937-b01f3088ed4a',
    },
    screenCosts: {
      subTotal: 'bdfdf42a-62e5-4b07-bc38-6537fc53ca96',
      price: '324919ad-6a63-4fff-aac2-ef0780720288',
    },
  },
  instructions: {
    wash: '248ccd96-4642-4724-bc29-1e91cb7b2859',
    insideNeck: 'dba3acb6-2b15-4cee-b5e5-808449c50a44',
    careLabel: '1b5d3e5c-b69d-4934-82bf-294264ef1c5d',
    sizeLabel: 'ffd974b6-ed42-4d06-a4a6-eff6752a9e12',
    trims: '67cd359c-8ab2-4ce0-9bfb-8807623dc885',
    packaging: '494d2791-16b8-4b3d-8984-661fac1019c4',
    approvals: '67d32b81-6b9a-4cb0-9d7e-570f2f7d1504',
    sample: 'bd34cb52-0987-4627-a014-b7e8e0f93b88',
    other: 'a94b093e-c74d-4962-beab-05e150ec4e9c',
  },
}
